import React, { useState, useEffect, useMemo } from "react";
import "./Dashboard.scss";
import {
  AreaChart,
  Area,
  YAxis,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import ChemIndexDashboardBarChart from "./feature/ChemIndexDashboardBarChart";
import ChemIndexDashboardGrowthChart from "./feature/ChemIndexDashboardGrowthChart";
import {
  useGetChemIndexDashboardCountsDetilsQuery,
  useGetChemProductsAvailableYearsQuery,
  useGetChemProductsMonthlyGrowthQuery,
} from "../../app/services/dashboardAPI";

// 🔢 Animated Counter Component
function AnimatedCounter({ value, duration = 1500 }) {
  const [count, setCount] = useState(0);

  useEffect(() => {
    let start = 0;
    const increment = value / (duration / 16);
    const timer = setInterval(() => {
      start += increment;
      if (start >= value) {
        clearInterval(timer);
        setCount(value);
      } else {
        setCount(Math.floor(start));
      }
    }, 16);

    return () => clearInterval(timer);
  }, [value, duration]);

  return <span>{count.toLocaleString()}</span>;
}

// 📈 Mini sparkline for KPI cards
const SparklineTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="spark-tooltip">
        <span className="spark-tooltip-label">{label}</span>
        <span className="spark-tooltip-value">
          {Number(payload[0].value).toLocaleString()}
        </span>
      </div>
    );
  }
  return null;
};

const Sparkline = ({ data, color }) => {
  const gradientId = `sparkGradient-${String(color).replace("#", "")}`;
  if (!data || data.length === 0) {
    return <div className="card-sparkline is-empty"></div>;
  }
  return (
    <div className="card-sparkline">
      <ResponsiveContainer width="100%" height={48}>
        <AreaChart data={data} margin={{ top: 5, right: 0, left: 0, bottom: 0 }}>
          <defs>
            <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={color} stopOpacity={0.35} />
              <stop offset="100%" stopColor={color} stopOpacity={0.02} />
            </linearGradient>
          </defs>
          <YAxis hide domain={["dataMin", "dataMax"]} />
          <Tooltip content={<SparklineTooltip />} cursor={false} />
          <Area
            type="monotone"
            dataKey="value"
            stroke={color}
            strokeWidth={2}
            fill={`url(#${gradientId})`}
            dot={false}
            animationDuration={1200}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
};

// 🏷️ Premium KPI card (gradient icon + animated value + sparkline + delta)
const CARD_PALETTES = {
  navy: { spark: "#1b6cd8" },
  blue: { spark: "#2196f3" },
  sky: { spark: "#25b2dc" },
  cyan: { spark: "#0ea5e9" },
};

const SummaryCard = ({ label, value, iconClass, variant, status, isFetching, sparkData, delta }) => {
  const sparkColor = CARD_PALETTES[variant]?.spark || "#1b6cd8";
  const hasDelta = delta !== null && delta !== undefined && !Number.isNaN(delta);
  const isUp = hasDelta && delta >= 0;

  return (
    <div className={`summary-card card-${variant}`}>
      <span className="card-status">{status}</span>
      <div className="card-row">
        <div className="card-icon">
          <i className={iconClass}></i>
        </div>
        <div className="card-meta">
          <div className="card-label">{label}</div>
          <div className="card-value">
            {isFetching ? "..." : <AnimatedCounter value={value} />}
          </div>
        </div>
      </div>
      <div className="card-footer">
        <div className="card-spark-wrap">
          <Sparkline data={sparkData} color={sparkColor} />
        </div>
        {hasDelta && (
          <div className={`delta-chip ${isUp ? "up" : "down"}`}>
            <i className={`bi ${isUp ? "bi-arrow-up-short" : "bi-arrow-down-short"}`}></i>
            {Math.abs(delta).toFixed(1)}%
            <span className="delta-caption">MoM</span>
          </div>
        )}
      </div>
    </div>
  );
};

// Side panel composition colors
const COMPOSITION_COLORS = ["#1b6cd8", "#1f9fe7", "#25b2dc", "#0d2340"];

const Dashboard = () => {
  const { isFetching, isSuccess, data } = useGetChemIndexDashboardCountsDetilsQuery();

  // Fetch available years for growth chart
  const { data: availableYearsData = [], isFetching: isFetchingYears } =
    useGetChemProductsAvailableYearsQuery();

  const years = useMemo(() => {
    if (!availableYearsData || availableYearsData.length === 0) return [];
    return availableYearsData.map((y) => y.year).sort();
  }, [availableYearsData]);

  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

  // Update selectedYear when available years load
  useEffect(() => {
    if (years.length && !years.includes(selectedYear)) {
      setSelectedYear(years[years.length - 1]);
    }
  }, [years, selectedYear]);

  // Growth series (RTK Query shares the cache with the growth chart component)
  const { data: growthSeries = [] } = useGetChemProductsMonthlyGrowthQuery(selectedYear, {
    skip: !selectedYear,
  });

  // Sparkline series and month-over-month delta from insert activity
  const sparkData = useMemo(() => {
    if (!growthSeries || growthSeries.length === 0) return [];
    return growthSeries.map((m) => ({ name: m.monthName, value: Number(m.inserts) }));
  }, [growthSeries]);

  const monthDelta = useMemo(() => {
    if (sparkData.length < 2) return null;
    const last = Number(sparkData[sparkData.length - 1].value);
    const prev = Number(sparkData[sparkData.length - 2].value);
    if (!prev) return null;
    return ((last - prev) / prev) * 100;
  }, [sparkData]);

  const getValueByLabel = (label) => {
    if (!isSuccess || !data) return 0;
    const item = data.find((d) => d.label === label);
    return item ? item.value : 0;
  };

  // Total Products is the base reference for composition percentages
  const totalProducts = getValueByLabel("ChemProducts");

  // Data composition for the side panel (Products is the base row, so excluded)
  const compositionItems = useMemo(() => {
    if (!isSuccess || !data || !Array.isArray(data)) return [];
    return data
      .filter((item) => item.label !== "ChemProducts")
      .map((item) => ({
        name: item.label,
        displayName: String(item.label).replace("Total", "").replace("ChemProducts", "Products"),
        value: item.value,
      }));
  }, [isSuccess, data]);

  // Full record count (all categories) used by the insight card
  const allRecordsTotal =
    isSuccess && Array.isArray(data)
      ? data.reduce((sum, item) => sum + Number(item.value || 0), 0)
      : 0;

  const peakMonth = useMemo(() => {
    if (!growthSeries || growthSeries.length === 0) return null;
    return growthSeries.reduce(
      (best, m) => (Number(m.inserts) > Number(best.inserts) ? m : best),
      growthSeries[0]
    );
  }, [growthSeries]);

  const ytdAdditions = useMemo(() => {
    if (!growthSeries || growthSeries.length === 0) return 0;
    return growthSeries.reduce((sum, m) => sum + Number(m.inserts), 0);
  }, [growthSeries]);

  const monthlyAverage = useMemo(() => {
    if (!growthSeries || growthSeries.length === 0) return 0;
    return Math.round(ytdAdditions / growthSeries.length);
  }, [growthSeries, ytdAdditions]);

  return (
    <div className="dashboard-container">

      {/* Page Header */}
      <div className="dashboard-header">
        <div className="header-left">
          <div className="dashboard-title">Analytics Dashboard</div>
          <div className="dashboard-subtitle">Live overview of the ChemIndex chemical dataset</div>
        </div>
        <div className="header-actions">
          <div className="live-badge">
            <span className="pulse-dot"></span>
            Live Data
          </div>
          <div className="updated-chip">
            <i className="bi bi-clock-history"></i>
            Updated just now
          </div>
        </div>
      </div>

      {/* KPI Cards with Sparklines */}
      <div className="dashboard-cards">
        <SummaryCard
          label="Total Products"
          value={getValueByLabel("ChemProducts")}
          iconClass="bi bi-box"
          variant="navy"
          status="LIVE"
          isFetching={isFetching}
          sparkData={sparkData}
          delta={monthDelta}
        />
        <SummaryCard
          label="Total Salts"
          value={getValueByLabel("Salts")}
          iconClass="bi bi-shield-fill-exclamation"
          variant="blue"
          status="ACTIVE"
          isFetching={isFetching}
          sparkData={sparkData}
          delta={monthDelta}
        />
        <SummaryCard
          label="Total StereoIsomers"
          value={getValueByLabel("StereoIsomers")}
          iconClass="bi bi-diagram-2-fill"
          variant="sky"
          status="UPDATED"
          isFetching={isFetching}
          sparkData={sparkData}
          delta={monthDelta}
        />
        <SummaryCard
          label="Tautomers"
          value={getValueByLabel("Tautomers")}
          iconClass="bi bi-diagram-3-fill"
          variant="cyan"
          status="STABLE"
          isFetching={isFetching}
          sparkData={sparkData}
          delta={monthDelta}
        />
      </div>

      {/* Main Grid: Charts + Side Panel */}
      <div className="dashboard-main-grid">

        {/* Chart column */}
        <div className="dashboard-charts-column">

          {/* Product Distribution Donut */}
          <div className="chart-card">
            <div className="chart-card-header">
              <div className="chart-heading">
                <h3 className="chart-title">Product Distribution Breakdown</h3>
                <p className="chart-subtitle">Share of records across the chemical dataset</p>
              </div>
              <div className="chart-head-icon">
                <i className="bi bi-pie-chart-fill"></i>
              </div>
            </div>
            <ChemIndexDashboardBarChart
              isGetDashboardCountsFetching={isFetching}
              data={data || []}
            />
          </div>

          {/* Monthly Growth Chart */}
          <div className="chart-card">
            <div className="chart-card-header">
              <div className="chart-heading">
                <h3 className="chart-title">Monthly Growth Statistics</h3>
                <p className="chart-subtitle">New inserts against cumulative records</p>
              </div>
              {!isFetchingYears && (
                <div className="year-selector">
                  <label>Period</label>
                  <div className="select-wrap">
                    <select
                      value={selectedYear}
                      onChange={(e) => setSelectedYear(Number(e.target.value))}
                    >
                      {years.map((yr) => (
                        <option key={yr} value={yr}>
                          FY {yr}
                        </option>
                      ))}
                    </select>
                    <i className="bi bi-chevron-down"></i>
                  </div>
                </div>
              )}
            </div>
            {isFetchingYears ? (
              <div className="spinner-wrapper">Loading growth data...</div>
            ) : (
              <ChemIndexDashboardGrowthChart selectedYear={selectedYear} />
            )}
          </div>
        </div>

        {/* Side Panel */}
        <aside className="dashboard-side-panel">
          <div className="panel-card">
            <div className="panel-card-header">
              <h4 className="panel-title">Data Composition</h4>
              <span className="panel-badge">{Number(totalProducts).toLocaleString()} products</span>
            </div>
            <div className="composition-list">
              {compositionItems.map((item, idx) => {
                const color = COMPOSITION_COLORS[idx % COMPOSITION_COLORS.length];
                const percent = totalProducts ? (item.value / totalProducts) * 100 : 0;
                const barWidth = Math.min(percent, 100);
                return (
                  <div className="composition-item" key={item.name}>
                    <div className="comp-row">
                      <span className="comp-name">
                        <span className="comp-dot" style={{ backgroundColor: color }}></span>
                        {item.displayName}
                      </span>
                      <span className="comp-value">{Number(item.value).toLocaleString()}</span>
                    </div>
                    <div className="comp-track">
                      <div
                        className="comp-fill"
                        style={{ width: `${barWidth}%`, backgroundColor: color }}
                      ></div>
                    </div>
                    <div className="comp-legend">
                      <span className="comp-percent">{percent.toFixed(1)}%</span>
                      <span className="comp-share">of products</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="panel-card">
            <div className="panel-card-header">
              <h4 className="panel-title">FY {selectedYear} Highlights</h4>
              <i className="bi bi-graph-up-arrow panel-header-icon"></i>
            </div>
            <div className="stats-grid">
              <div className="stat-item">
                <span className="stat-label">Peak Month</span>
                <span className="stat-value">{peakMonth ? peakMonth.monthName : "—"}</span>
                <span className="stat-sub">
                  {peakMonth ? `${Number(peakMonth.inserts).toLocaleString()} inserts` : "Not available"}
                </span>
              </div>
              <div className="stat-item">
                <span className="stat-label">Year-to-date</span>
                <span className="stat-value">
                  {ytdAdditions ? ytdAdditions.toLocaleString() : "—"}
                </span>
                <span className="stat-sub">new records added</span>
              </div>
              <div className="stat-item">
                <span className="stat-label">Monthly Average</span>
                <span className="stat-value">
                  {monthlyAverage ? monthlyAverage.toLocaleString() : "—"}
                </span>
                <span className="stat-sub">records per month</span>
              </div>
              <div className="stat-item">
                <span className="stat-label">Total Products</span>
                <span className="stat-value">
                  {totalProducts ? Number(totalProducts).toLocaleString() : "—"}
                </span>
                <span className="stat-sub">composition base</span>
              </div>
            </div>
          </div>

          <div className="panel-card panel-summary">
            <div className="panel-card-header">
              <h4 className="panel-title">Dataset Insight</h4>
              <i className="bi bi-lightbulb panel-header-icon light"></i>
            </div>
            <p className="insight-text">
              ChemIndex currently holds{" "}
              <strong>{Number(allRecordsTotal).toLocaleString()}</strong> chemical records. Products make
              up the largest segment, with{" "}
              <strong>{ytdAdditions ? ytdAdditions.toLocaleString() : "ongoing"}</strong> additions
              recorded during <strong>FY {selectedYear}</strong>.
            </p>
          </div>
        </aside>
      </div>
    </div>
  );
};

export default Dashboard;
